import { useCallback } from "react";

import { ClientsService, type InternalClientsListParams } from "../services/clientsService";
import type { ClientListItem } from "../types/clients-api.types";
import { normalizeClientListItem } from "../utils/normalizeClient";
import { usePaginatedClientsQuery, type PaginatedClientsQuery } from "./usePaginatedClientsQuery";

/**
 * Level 2 of the master-detail: server-paginated clients of one organization (GET
 * /internal/clients?organization_id=…). For organization users this is also the root view — omit
 * `organizationId` and the backend scopes the list by the JWT organization.
 *
 * Filters applied by the backend: `type`, `country`, and fuzzy `search` (name/email/identification).
 * `kyc_status` and `verified_only` exist in the contract but are not applied in QA yet (see
 * docs/clients-view-gaps.md).
 */
export function useOrganizationClients(organizationId?: string): PaginatedClientsQuery<ClientListItem> {
  const fetcher = useCallback(
    async (params: InternalClientsListParams) => {
      const response = await ClientsService.fetchList(
        organizationId ? { ...params, organization_id: organizationId } : params,
      );
      return {
        rows: response.data.data.map(normalizeClientListItem),
        total: response.data.total,
      };
    },
    [organizationId],
  );

  return usePaginatedClientsQuery(fetcher);
}
